<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  resolveComponent,
  type PropType,
  watch,
} from 'vue';
import tabApostatudo from '@/assets/json/tabs-apostatudo.json';
import type { GameItem } from '@/utils/perfilOficialConfigs';
import PerfilOficialSwiperHeader from '@/components/perfil-oficial/SwiperHeader.vue';

const NuxtLink = resolveComponent('NuxtLink');

const { isMobile } = useLayout();
const { openPreview } = useGameMobilePreview();

interface TabData {
  title: string;
  child: GameItem[];
  icon?: string;
  slug?: string; // Slug da categoria para URL dinâmica
  see_more_url?: string | null; // URL customizada do "Ver Mais" (item_config.see_more_url)
}

const tabsNavigation = ref<HTMLElement | null>(null);
const gamesGrid = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const isDraggingGames = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const startXGames = ref(0);
const scrollLeftGames = ref(0);
const dragged = ref(false);
const showTabsFadeLeft = ref(false);
const showTabsFadeRight = ref(false);
const showGamesFadeLeft = ref(false);
const showGamesFadeRight = ref(false);
const isComponentReady = ref(false); // Prevents animations during initial load

const props = defineProps({
  tabs: {
    type: Array as PropType<TabData[]>,
    default: null,
  },
  headerTitle: {
    type: String,
    default: undefined,
  },
  headerIconPath: {
    type: String,
    default: undefined,
  },
  headerSeeMoreUrl: {
    type: String,
    default: undefined,
  },
  isTabVisible: {
    type: Boolean,
    default: true,
  },
  loadingStrategy: {
    type: String as PropType<'eager' | 'lazy'>,
    default: 'lazy',
  },
});

const { getLoadingStrategy } = useImageLoading();

// TabsApostatudo usa lazy loading por padrão, mas pode ser configurado para eager
const effectiveItemsAboveFold = computed(() => {
  return props.loadingStrategy === 'eager' ? 999 : 0;
});

const componentTabs = computed<TabData[]>(() => {
  const source = props.tabs || tabApostatudo.tabs;

  return source.filter((section) => section.child && section.child.length > 0);
});

const activeTab = ref(0);

const currentGames = computed(() => {
  // Retorna jogos do tab ativo, se válido
  if (
    activeTab.value >= 0 &&
    activeTab.value < componentTabs.value.length &&
    componentTabs.value.length > 0
  ) {
    return componentTabs.value[activeTab.value]?.child || [];
  }

  return [];
});

// URL dinâmica do "Ver Mais" baseada na tab ativa
const dynamicSeeMoreUrl = computed(() => {
  const activeTabData = componentTabs.value[activeTab.value];

  // Prioridade 1: URL customizada do item_config da tab (quando tem múltiplas tabs)
  if (activeTabData?.see_more_url) {
    return activeTabData.see_more_url;
  }

  // Prioridade 2: URL baseada no slug da tab
  if (activeTabData?.slug) {
    return `/games/category/${activeTabData.slug}`;
  }

  // Prioridade 3: Fallback para URL fixa (headerSeeMoreUrl) ou undefined
  return props.headerSeeMoreUrl;
});

// Icon path dinâmico baseado na tab ativa
const dynamicIconPath = computed(() => {
  const activeTabData = componentTabs.value[activeTab.value];

  // Se a tab tem icon, usa ele
  if (activeTabData?.icon) {
    return activeTabData.icon;
  }

  // Fallback para icon fixo (headerIconPath) ou undefined
  return props.headerIconPath;
});

// Corrige activeTab se necessário
watch(
  componentTabs,
  (tabs) => {
    if (activeTab.value >= tabs.length) {
      activeTab.value = 0;
    }
  },
  { immediate: true },
);

function getVerTodosSlug() {
  const activeTabData = componentTabs.value[activeTab.value];
  let slug = '/games/category/all';

  if (activeTabData?.see_more_url) {
    slug = activeTabData.see_more_url;
  } else if (activeTabData?.slug) {
    slug = `/games/category/${activeTabData.slug}`;
  }

  return slug;
}

const verTodosCard = computed(() => ({
  id: 'placeholder',
  name: 'ApostaTudo',
  slug: getVerTodosSlug(),
  image: '',
}));

// Queridinhos: número de rows dinâmico baseado na quantidade de jogos
// 3 rows se tem 11+ jogos, senão 2 rows — sempre 4 colunas
const queridinhosRows = computed(() => {
  if (props.headerTitle !== 'Queridinhos') return 2;

  return currentGames.value.length >= 11 ? 3 : 2;
});

const processedGames = computed(() => {
  const games = currentGames.value;

  // Queridinhos: sempre 4 colunas, 3 rows (12 itens) ou 2 rows (8 itens)
  if (props.headerTitle === 'Queridinhos') {
    const maxGames = games.length >= 11 ? 11 : 7;
    const sliced = games.slice(0, maxGames);

    return [...sliced, verTodosCard.value];
  }

  if (games.length >= 7 && games.length % 2 !== 0) {
    return [...games, verTodosCard.value];
  }

  return games;
});

const gridCols = computed(() => {
  return Math.ceil(processedGames.value.length / queridinhosRows.value);
});

function selectTab(index: number) {
  if (dragged.value) {
    dragged.value = false;

    return;
  }
  if (activeTab.value === index) {
    return;
  }
  nextTick(() => {
    activeTab.value = index;
  });
}

function onMouseDown(e: MouseEvent) {
  if (!tabsNavigation.value) return;
  isDragging.value = true;
  startX.value = e.pageX - tabsNavigation.value.offsetLeft;
  scrollLeft.value = tabsNavigation.value.scrollLeft;
  tabsNavigation.value.style.cursor = 'grabbing';
  tabsNavigation.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value || !tabsNavigation.value) return;
  e.preventDefault();

  const x = e.pageX - tabsNavigation.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    // Threshold to prevent click on small drag
    dragged.value = true;
  }
  tabsNavigation.value.scrollLeft = scrollLeft.value - walk * 1;
}

function onMouseUp() {
  if (!tabsNavigation.value) return;
  isDragging.value = false;
  tabsNavigation.value.style.cursor = 'grab';
  tabsNavigation.value.style.userSelect = 'auto';
}

const currentGamesScrollLeft = ref(0);
const currentGamesScrollWidth = ref(0);
const currentGamesClientWidth = ref(0);

function updateGamesScrollMetrics() {
  if (gamesGrid.value) {
    currentGamesScrollLeft.value = gamesGrid.value.scrollLeft;
    currentGamesScrollWidth.value = gamesGrid.value.scrollWidth;
    currentGamesClientWidth.value = gamesGrid.value.clientWidth;
  }
}

function scrollNextGames() {
  if (gamesGrid.value) {
    const scrollAmount = gamesGrid.value.clientWidth * 0.4;

    gamesGrid.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
}

function scrollPrevGames() {
  if (gamesGrid.value) {
    const scrollAmount = gamesGrid.value.clientWidth * 0.4;

    gamesGrid.value.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }
}

const swiperControls = {
  scrollNext: scrollNextGames,
  scrollPrev: scrollPrevGames,
  currentScrollLeft: currentGamesScrollLeft,
  currentScrollWidth: currentGamesScrollWidth,
  currentClientWidth: currentGamesClientWidth,
};

function onMouseDownGames(e: MouseEvent) {
  if (!gamesGrid.value) return;
  isDraggingGames.value = true;
  startXGames.value = e.pageX - gamesGrid.value.offsetLeft;
  scrollLeftGames.value = gamesGrid.value.scrollLeft;
  gamesGrid.value.style.cursor = 'grabbing';
  gamesGrid.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMoveGames(e: MouseEvent) {
  if (!isDraggingGames.value || !gamesGrid.value) return;
  e.preventDefault();

  const x = e.pageX - gamesGrid.value.offsetLeft;
  const walk = x - startXGames.value;

  if (Math.abs(walk) > 5) {
    // Threshold to prevent click on small drag
    dragged.value = true;
  }
  gamesGrid.value.scrollLeft = scrollLeftGames.value - walk * 1;
  updateGamesScrollMetrics();
}

function onMouseUpGames() {
  if (!gamesGrid.value) return;
  isDraggingGames.value = false;
  gamesGrid.value.style.cursor = 'grab';
  gamesGrid.value.style.userSelect = 'auto';
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
}

function onMouseLeaveGames() {
  if (!gamesGrid.value) return;
  isDraggingGames.value = false;
  gamesGrid.value.style.cursor = 'grab';
  gamesGrid.value.style.userSelect = 'auto';
}

function checkScrollFades(
  el: HTMLElement | null,
  leftFadeRef: Ref<boolean>,
  rightFadeRef: Ref<boolean>,
  updateMetricsFn?: () => void,
) {
  if (!el) return;

  const hasOverflow = el.scrollWidth > el.clientWidth;

  leftFadeRef.value = hasOverflow && el.scrollLeft > 0;
  rightFadeRef.value =
    hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth;

  if (updateMetricsFn) {
    updateMetricsFn();
  }
}

onMounted(() => {
  nextTick(() => {
    checkScrollFades(tabsNavigation.value, showTabsFadeLeft, showTabsFadeRight);
    checkScrollFades(
      gamesGrid.value,
      showGamesFadeLeft,
      showGamesFadeRight,
      updateGamesScrollMetrics,
    );

    // Enable animations after a short delay to prevent CLS during initial render
    setTimeout(() => {
      isComponentReady.value = true;
    }, 100);
  });
  tabsNavigation.value?.addEventListener('scroll', () =>
    checkScrollFades(tabsNavigation.value, showTabsFadeLeft, showTabsFadeRight),
  );
  gamesGrid.value?.addEventListener('scroll', () =>
    checkScrollFades(
      gamesGrid.value,
      showGamesFadeLeft,
      showGamesFadeRight,
      updateGamesScrollMetrics,
    ),
  );
  window.addEventListener('resize', () => {
    checkScrollFades(tabsNavigation.value, showTabsFadeLeft, showTabsFadeRight);
    checkScrollFades(
      gamesGrid.value,
      showGamesFadeLeft,
      showGamesFadeRight,
      updateGamesScrollMetrics,
    );
  });
});
onUnmounted(() => {
  tabsNavigation.value?.removeEventListener('scroll', () =>
    checkScrollFades(tabsNavigation.value, showTabsFadeLeft, showTabsFadeRight),
  );
  gamesGrid.value?.removeEventListener('scroll', () =>
    checkScrollFades(
      gamesGrid.value,
      showGamesFadeLeft,
      showGamesFadeRight,
      updateGamesScrollMetrics,
    ),
  );
  window.removeEventListener('resize', () => {
    checkScrollFades(tabsNavigation.value, showTabsFadeLeft, showTabsFadeRight);
    checkScrollFades(
      gamesGrid.value,
      showGamesFadeLeft,
      showGamesFadeRight,
      updateGamesScrollMetrics,
    );
  });
});
function isVerTodosCard(game: GameItem): boolean {
  return game.id === 'placeholder' || game.name === 'ApostaTudo';
}

// "Ver Todos" sempre navega (não tem preview). Cards de jogo: no mobile vira
// <div> pra abrir o preview; no desktop continua como NuxtLink navegando.
function shouldRenderGameAsLink(game: GameItem): boolean {
  if (isVerTodosCard(game)) return true;

  return !isMobile.value;
}

function handleGameClick(game: GameItem, event: MouseEvent) {
  if (dragged.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    dragged.value = false;

    return;
  }

  if (!isVerTodosCard(game) && isMobile.value) {
    openPreview(game as unknown as Game);
  }
}
</script>

<template>
  <div class="tabsApostatudo">
    <PerfilOficialSwiperHeader
      v-if="headerTitle"
      :icon-path="dynamicIconPath"
      :see-more-url="dynamicSeeMoreUrl"
      :show-navigation="false"
      :swiper-ref="swiperControls"
      :title="headerTitle"
    />

    <div
      v-if="isTabVisible"
      ref="tabsNavigation"
      class="tabsApostatudo__tabs-navigation"
      :class="{
        'fade-left': showTabsFadeLeft,
        'fade-right': showTabsFadeRight,
      }"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
    >
      <BaseFilterTabButton
        v-for="(section, index) in componentTabs"
        :key="index"
        :active="activeTab === index"
        :label="section.title"
        @click="selectTab(index)"
      />
    </div>

    <div
      ref="gamesGrid"
      class="tabsApostatudo__content"
      :class="{
        'fade-left': showGamesFadeLeft,
        'fade-right': showGamesFadeRight,
      }"
      @mousedown="onMouseDownGames"
      @mouseleave="onMouseLeaveGames"
      @mousemove="onMouseMoveGames"
      @mouseup="onMouseUpGames"
    >
      <div
        class="tabsApostatudo__games-grid"
        :class="{
          'tabsApostatudo__games-grid--three-rows': queridinhosRows === 3,
        }"
        :style="{ '--grid-cols': gridCols }"
      >
        <component
          :is="shouldRenderGameAsLink(game) ? NuxtLink : 'div'"
          v-for="(game, index) in processedGames"
          :key="game.id"
          class="tabsApostatudo__game-card"
          :class="{
            dragging: dragged,
            'tabsApostatudo__game-card--placeholder':
              game.id === 'placeholder' || game.name === 'ApostaTudo',
            'tabsApostatudo__game-card--shiny':
              isComponentReady &&
              index === 0 &&
              activeTab === 0 &&
              headerTitle === 'Queridinhos',
            'tabsApostatudo__game-card--mobile': !shouldRenderGameAsLink(game),
          }"
          draggable="false"
          v-bind="
            shouldRenderGameAsLink(game)
              ? {
                  to:
                    game.id === 'placeholder'
                      ? game.slug
                      : `/games/${game.slug}`,
                }
              : {}
          "
          @click="handleGameClick(game, $event)"
        >
          <div
            v-if="game.id === 'placeholder' || game.name === 'ApostaTudo'"
            class="tabsApostatudo__ver-todos"
          >
            <div class="tabsApostatudo__ver-todos-icon">
              <svg
                class="w-[16px] h-[16px] md:w-[20px] md:h-[20px]"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 5V19M5 12H19"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="2.5"
                />
              </svg>
            </div>
            <span class="tabsApostatudo__ver-todos-text"
              >VER TODOS<br />OS JOGOS</span
            >
          </div>
          <CasinoMediaApostatudo
            v-else
            class="tabsApostatudo__game-image-component"
            :game="game as Game"
            :index="index"
            :loading="getLoadingStrategy(index, effectiveItemsAboveFold)"
            no-link
          />
        </component>
      </div>
    </div>
  </div>
</template>

<style>
.tabsApostatudo {
  border-radius: 8px;
  margin-bottom: 16px;
  min-height: 236px; /* Header (~32px) + margin + grid (204px) */
}

@media (min-width: 768px) {
  .tabsApostatudo {
    min-height: 176px; /* Header (~52px) + margin + grid (124px) */
  }
}
.tabsApostatudo__tabs-navigation {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
}
.tabsApostatudo__tabs-navigation::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari, Opera */
}
.tabsApostatudo__content {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
}
.tabsApostatudo__content::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari, Opera */
}
.tabsApostatudo__games-grid {
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(var(--grid-cols), auto);
  grid-template-rows: repeat(2, auto);
  gap: 4px;
}
@media (max-width: 767px) {
  .tabsApostatudo__games-grid--three-rows {
    grid-template-rows: repeat(3, auto);
  }
}
.tabsApostatudo__game-card.dragging {
  pointer-events: none;
}
.tabsApostatudo__game-card--mobile {
  cursor: pointer;
}
.tabsApostatudo__tabs-navigation.fade-left {
  /* Fade removido */
}
.tabsApostatudo__tabs-navigation.fade-right {
  /* Fade removido */
}
.tabsApostatudo__tabs-navigation.fade-left.fade-right {
  /* Fade removido */
}
.tabsApostatudo__content.fade-left {
  /* Fade removido */
}
.tabsApostatudo__content.fade-right {
  /* Fade removido */
}
.tabsApostatudo__content.fade-left.fade-right {
  /* Fade removido */
}
.tabsApostatudo__game-card {
  overflow: hidden;
  text-align: center;
  width: calc(
    (min(100vw, 690px) - 2 * var(--container-padding, 16px) - 3 * 4px) / 4
  );
  aspect-ratio: 6 / 7;
  flex-shrink: 0; /* Prevent cards from shrinking */
  display: flex;
  flex-direction: column;
  justify-content: center; /* Center image vertically */
  border-radius: 4px;
  position: relative;
}
.tabsApostatudo__game-card--placeholder {
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  background: linear-gradient(
    0deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}

.tabsApostatudo__ver-todos {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 8px;
  padding: 8px;
}

.tabsApostatudo__ver-todos-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0062ff;
  color: #0a0a1a;
}

.tabsApostatudo__ver-todos-text {
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  text-transform: uppercase;
}

@media (min-width: 768px) {
  .tabsApostatudo__ver-todos {
    gap: 10px;
  }

  .tabsApostatudo__ver-todos-icon {
    width: 36px;
    height: 36px;
  }

  .tabsApostatudo__ver-todos-text {
    font-size: 13px;
  }
}
.tabsApostatudo__game-image {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  margin-bottom: 0;
  object-fit: cover;
}
.tabsApostatudo__game-image-component {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  aspect-ratio: unset;
}

@media (min-width: 768px) {
  .tabsApostatudo {
    margin-bottom: 20px;
    min-height: 220px; /* Header (~60px) + margin + grid (160px) */
  }
  .tabsApostatudo__games-grid {
    grid-auto-flow: column;
    grid-template-columns: none;
    grid-template-rows: 1fr; /* Single row for desktop */
    gap: 8px;
    height: 160px; /* Fixed height: 1 card height */
  }
  .tabsApostatudo__game-card {
    width: 136px; /* Desktop width */
    height: 160px; /* Desktop height */
  }
  .tabsApostatudo__game-image {
    height: 100%; /* Image takes full height of card */
  }
  .tabsApostatudo__tabs-navigation {
    margin-bottom: 8px;
  }
}
.animation-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.animation-controls button {
  padding: 5px 10px;
  border-radius: 6px;
  background: #333;
  color: #fff;
  border: 1px solid #555;
  cursor: pointer;
}

/* Mobile: faz o swiper encostar na lateral direita */
@media (max-width: 414px) {
  .tabsApostatudo__tabs-navigation,
  .tabsApostatudo__content {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }
}

/* Shimmer shine effect for first card in Queridinhos */
.tabsApostatudo__game-card--shiny {
  position: relative;
  overflow: hidden;
  contain: layout style paint; /* Optimize rendering */
}

.tabsApostatudo__game-card--shiny::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -100%;
  width: 50%;
  height: 200%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  transform: skewX(-25deg);
  animation: shimmerSlide 1.5s ease-out infinite;
  pointer-events: none;
  z-index: 2;
  will-change: left; /* Optimize animation */
}

.tabsApostatudo__game-card--shiny::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-shadow:
    inset 0 0 20px rgba(255, 255, 255, 0.3),
    0 0 15px rgba(0, 150, 255, 0.5);
  border-radius: 4px;
  animation: glowPulse 2s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
  will-change: opacity; /* Optimize animation */
}

@keyframes shimmerSlide {
  0% {
    left: -100%;
  }
  100% {
    left: 150%;
  }
}

@keyframes glowPulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
